import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { doctorsApi, filesApi } from '@/api';
import type { DoctorListItem, Specialty } from '@/api';
import { usePatientLocation } from '@/hooks/usePatientLocation';
import { Button } from '@medikei/ui';
import { Card } from '@medikei/ui';
import { Badge } from '@medikei/ui';
import { Skeleton } from '@medikei/ui';
import { EmptyState } from '@/components/ui/empty-state';
import { Header } from '@/landing/v3/sections/Header';
import { LocationPromptModal } from '@/components/location/LocationPromptModal';
import { GlobalSalesBanner } from '@/components/common/GlobalSalesBanner';
import {
  Search,
  Star,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Stethoscope,
  ArrowLeft,
  MapPin,
  Navigation,
  Video,
  Home,
  Building,
  Heart,
  Brain,
  Baby,
  Bone,
  Eye,
  Pill,
  Sparkles,
  Shield,
  Activity,
  Check,
  Loader2,
  X,
  Crosshair,
} from 'lucide-react';
import { cn } from '@/utils/cn';

const POPULAR_CITIES = [
  'São Paulo, SP',
  'Rio de Janeiro, RJ',
  'Belo Horizonte, MG',
  'Curitiba, PR',
  'Brasília, DF',
  'Porto Alegre, RS',
  'Salvador, BA',
  'Campinas, SP',
];

function getSpecialtyIcon(iconName?: string | null) {
  switch (iconName?.toLowerCase()) {
    case 'heart':
      return <Heart className="h-4 w-4 text-red-500 shrink-0" />;
    case 'brain':
      return <Brain className="h-4 w-4 text-purple-500 shrink-0" />;
    case 'baby':
      return <Baby className="h-4 w-4 text-amber-500 shrink-0" />;
    case 'bone':
      return <Bone className="h-4 w-4 text-blue-500 shrink-0" />;
    case 'eye':
      return <Eye className="h-4 w-4 text-cyan-500 shrink-0" />;
    case 'pill':
      return <Pill className="h-4 w-4 text-emerald-500 shrink-0" />;
    case 'sparkles':
      return <Sparkles className="h-4 w-4 text-pink-500 shrink-0" />;
    case 'shield':
      return <Shield className="h-4 w-4 text-indigo-500 shrink-0" />;
    case 'stethoscope':
      return <Stethoscope className="h-4 w-4 text-primary shrink-0" />;
    case 'activity':
    default:
      return <Activity className="h-4 w-4 text-primary shrink-0" />;
  }
}

export function MarketplacePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [doctors, setDoctors] = useState<DoctorListItem[]>([]);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 20,
    total: 0,
    pages: 0,
  });

  const searchQuery = searchParams.get('q') || '';
  const selectedSpecialty = searchParams.get('specialty') || '';
  const selectedLanguage = searchParams.get('language') || '';
  const selectedRating = searchParams.get('rating') || '';
  const selectedModality = searchParams.get('modality') || '';
  const selectedCity = searchParams.get('city') || '';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  const [searchInput, setSearchInput] = useState(searchQuery);
  const [locationInput, setLocationInput] = useState(selectedCity || '');
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const locationPickerRef = useRef<HTMLDivElement>(null);

  const {
    coords: userCoords,
    cep: patientCep,
    addressLabel,
    isLocating,
    hasLocation,
    requestGpsLocation,
    setCep,
  } = usePatientLocation();
  const [cepInput, setCepInput] = useState(patientCep);

  useEffect(() => {
    setCepInput(patientCep);
  }, [patientCep]);

  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    if (selectedCity) {
      setLocationInput(selectedCity);
    } else if (patientCep) {
      setLocationInput(`CEP: ${patientCep}`);
    } else if (addressLabel) {
      setLocationInput(addressLabel);
    } else {
      setLocationInput('');
    }
  }, [selectedCity, patientCep, addressLabel]);

  // Fechar popover de localização ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (locationPickerRef.current && !locationPickerRef.current.contains(event.target as Node)) {
        setShowLocationDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCepSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const cleaned = cepInput.replace(/\D/g, '');
    if (cleaned.length >= 8) {
      setCep(cleaned);
      setLocationInput(`CEP: ${cleaned}`);
      setShowLocationDropdown(false);
      setSearchParams((prev) => {
        prev.set('cep', cleaned);
        prev.delete('city');
        prev.set('page', '1');
        return prev;
      });
    }
  };

  useEffect(() => {
    const loadSpecialties = async () => {
      try {
        const data = await doctorsApi.getSpecialties();
        setSpecialties(data || []);
      } catch {
        // Handle error
      }
    };
    loadSpecialties();
  }, []);

  useEffect(() => {
    const loadDoctors = async () => {
      setIsLoading(true);
      try {
        const queryLat = searchParams.get('lat') ? parseFloat(searchParams.get('lat')!) : userCoords?.lat;
        const queryLng = searchParams.get('lng') ? parseFloat(searchParams.get('lng')!) : userCoords?.lng;
        const queryCep = searchParams.get('cep') || patientCep;

        const effectiveHasLocation = hasLocation || (queryLat !== undefined && queryLng !== undefined) || Boolean(queryCep);
        const isGeoSearch = (selectedModality === 'DOMICILIAR' || selectedModality === 'PRESENCIAL') && effectiveHasLocation;

        const data = await doctorsApi.getDoctors({
          search: searchQuery || undefined,
          specialty: selectedSpecialty || undefined,
          city: selectedCity || undefined,
          language: selectedLanguage || undefined,
          rating: selectedRating ? parseInt(selectedRating, 10) : undefined,
          modality: selectedModality || undefined,
          latitude: queryLat,
          longitude: queryLng,
          cep: queryCep || undefined,
          orderBy: isGeoSearch ? 'distance' : undefined,
          page: currentPage,
          limit: 20,
        });
        const doctorList = Array.isArray(data)
          ? data
          : Array.isArray((data as any)?.data)
          ? (data as any).data
          : Array.isArray((data as any)?.doctors)
          ? (data as any).doctors
          : [];
        setDoctors(doctorList);
        setPagination((data as any)?.meta || { page: 1, perPage: 20, total: doctorList.length, pages: 1 });
      } catch {
        setDoctors([]);
      } finally {
        setIsLoading(false);
      }
    };
    loadDoctors();
  }, [searchParams, searchQuery, selectedSpecialty, selectedCity, selectedLanguage, selectedRating, selectedModality, patientCep, userCoords?.lat, userCoords?.lng, currentPage, hasLocation]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams((prev) => {
      const q = searchInput.trim();
      if (q) {
        prev.set('q', q);
      } else {
        prev.delete('q');
      }

      const loc = locationInput.trim();
      if (loc) {
        if (loc.startsWith('CEP:')) {
          const rawCep = loc.replace(/\D/g, '');
          if (rawCep) prev.set('cep', rawCep);
          prev.delete('city');
        } else {
          prev.set('city', loc);
          prev.delete('cep');
        }
      } else {
        prev.delete('city');
        prev.delete('cep');
        prev.delete('lat');
        prev.delete('lng');
      }

      prev.set('page', '1');
      return prev;
    });
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchParams((prev) => {
      prev.delete('q');
      prev.set('page', '1');
      return prev;
    });
  };

  const handleClearLocation = () => {
    setLocationInput('');
    setSearchParams((prev) => {
      prev.delete('city');
      prev.delete('cep');
      prev.delete('lat');
      prev.delete('lng');
      prev.set('page', '1');
      return prev;
    });
  };

  const handleClearAllFilters = () => {
    setSearchInput('');
    setLocationInput('');
    setSearchParams({});
  };

  const handleSpecialtyFilter = (specialtyIdentifier: string) => {
    setSearchParams((prev) => {
      if (
        !specialtyIdentifier ||
        specialtyIdentifier === selectedSpecialty ||
        specialtyIdentifier.toLowerCase() === selectedSpecialty.toLowerCase()
      ) {
        prev.delete('specialty');
      } else {
        prev.set('specialty', specialtyIdentifier);
      }
      prev.set('page', '1');
      return prev;
    });
  };

  const handlePageChange = (newPage: number) => {
    setSearchParams((prev) => {
      prev.set('page', String(newPage));
      return prev;
    });
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-1 text-sm text-gray-600">({rating.toFixed(1)})</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <Header />
      <div className="flex-1 container-landing w-full py-4 sm:py-6 lg:py-8 px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb / Back Link */}
        <div className="mb-4 sm:mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-primary transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Voltar para a página inicial</span>
          </Link>
        </div>

        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-black text-foreground mb-1.5 tracking-tight">Buscar Médicos</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Encontre o médico ideal para você
          </p>
        </div>

        {/* Formulário Unificado de Busca: Especialidade/Médico + Localização/Cidade/CEP */}
        <form
          onSubmit={handleSearch}
          className="mb-6 p-2 sm:p-2.5 rounded-2xl bg-card border border-border shadow-md flex flex-col md:flex-row items-stretch gap-2 text-foreground relative z-20"
        >
          {/* Campo 1: Especialidade, Médico ou Serviço */}
          <div className="relative flex-1 px-4 py-2 rounded-xl hover:bg-muted/30 border border-border md:border-transparent transition-colors flex items-center justify-between">
            <Search className="w-4 h-4 text-muted-foreground mr-2.5 shrink-0" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Buscar por médico, especialidade ou CRM..."
              className="w-full text-xs sm:text-sm font-medium bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none"
            />
            {searchInput && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="p-1 text-muted-foreground hover:text-foreground cursor-pointer"
                title="Limpar busca"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="hidden md:block w-[1px] bg-border my-1.5" />

          {/* Campo 2: Localidade (Cidade, Estado, CEP ou GPS) */}
          <div
            ref={locationPickerRef}
            className="relative flex-1 px-4 py-2 rounded-xl hover:bg-muted/30 border border-border md:border-transparent transition-colors flex items-center justify-between cursor-pointer"
            onClick={() => setShowLocationDropdown(true)}
          >
            <MapPin className="w-4 h-4 text-primary mr-2.5 shrink-0" />
            <div className="w-full text-left">
              <input
                type="text"
                value={locationInput}
                onChange={(e) => setLocationInput(e.target.value)}
                onFocus={() => setShowLocationDropdown(true)}
                placeholder="Cidade, estado ou CEP..."
                className="w-full text-xs sm:text-sm font-medium bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none"
              />
            </div>

            {locationInput && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearLocation();
                }}
                className="p-1 text-muted-foreground hover:text-foreground cursor-pointer mr-0.5"
                title="Limpar localização"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowLocationDropdown((prev) => !prev);
              }}
              className="p-1 -mr-1 text-muted-foreground hover:text-foreground cursor-pointer"
              title="Opções de localização"
            >
              <ChevronDown
                className={cn(
                  'w-4 h-4 text-muted-foreground shrink-0 transition-transform duration-200',
                  showLocationDropdown && 'rotate-180'
                )}
              />
            </button>

            {/* Popover de Localização / Cidades / GPS */}
            {showLocationDropdown && (
              <div
                onClick={(e) => e.stopPropagation()}
                className="absolute top-full left-0 right-0 md:w-80 max-w-[calc(100vw-2rem)] mt-2 bg-card border border-border rounded-2xl shadow-2xl p-3 z-50 text-foreground space-y-2.5 animate-in fade-in duration-150"
              >
                {/* Botão de Localização Atual (GPS) */}
                <button
                  type="button"
                  onClick={async () => {
                    const coords = await requestGpsLocation();
                    if (coords) {
                      setShowLocationDropdown(false);
                      setSearchParams((prev) => {
                        prev.set('lat', coords.lat.toString());
                        prev.set('lng', coords.lng.toString());
                        prev.delete('city');
                        prev.set('page', '1');
                        return prev;
                      });
                    }
                  }}
                  disabled={isLocating}
                  className="w-full px-3 py-2.5 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 text-emerald-700 dark:text-emerald-400 text-xs font-bold flex items-center gap-2.5 transition-all cursor-pointer text-left"
                >
                  {isLocating ? (
                    <Loader2 className="w-4 h-4 animate-spin text-emerald-600 shrink-0" />
                  ) : (
                    <Crosshair className="w-4 h-4 text-emerald-600 shrink-0" />
                  )}
                  <div className="flex-1">
                    <span className="block font-bold">Usar minha localização atual (GPS)</span>
                    <span className="text-[10px] text-muted-foreground font-normal">
                      Raio de proximidade por satélite
                    </span>
                  </div>
                </button>

                {/* Campo Rápido de CEP */}
                <div className="pt-2 border-t border-border">
                  <label className="block text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1.5">
                    Ou buscar por CEP
                  </label>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="text"
                      placeholder="00000-000"
                      maxLength={9}
                      value={cepInput}
                      onChange={(e) => setCepInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleCepSubmit(e);
                        }
                      }}
                      className="flex-1 h-8 px-2.5 text-xs border border-border rounded-lg bg-muted/40 text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => handleCepSubmit()}
                      className="h-8 px-3 text-xs"
                    >
                      OK
                    </Button>
                  </div>
                </div>

                {/* Cidades Populares */}
                <div className="pt-2 border-t border-border">
                  <span className="block text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">
                    Cidades Principais
                  </span>
                  <div className="space-y-0.5 max-h-40 overflow-y-auto pr-1">
                    {POPULAR_CITIES.map((city) => (
                      <button
                        key={city}
                        type="button"
                        onClick={() => {
                          setLocationInput(city);
                          setShowLocationDropdown(false);
                          setSearchParams((prev) => {
                            prev.set('city', city);
                            prev.delete('cep');
                            prev.delete('lat');
                            prev.delete('lng');
                            prev.set('page', '1');
                            return prev;
                          });
                        }}
                        className={cn(
                          'w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center justify-between cursor-pointer',
                          locationInput === city
                            ? 'bg-primary/10 text-primary font-bold'
                            : 'text-foreground hover:bg-muted'
                        )}
                      >
                        <span>{city}</span>
                        {locationInput === city && <Check className="w-3.5 h-3.5 text-primary" />}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <Button type="submit" className="h-11 px-7 rounded-xl font-bold text-xs sm:text-sm w-full md:w-auto">
            Buscar
          </Button>
        </form>

        {/* Banner do Motor de Vendas posicionado logo abaixo do formulário de busca */}
        <div className="mb-6">
          <GlobalSalesBanner className="px-0 pt-0 pb-0" placement="LANDING" />
        </div>

        {/* Barra de Modalidades & Proximidade Geográfica */}
        <div className="mb-6 p-3 sm:p-4 rounded-2xl bg-card border border-border shadow-xs space-y-3 overflow-hidden">
          {/* Linha Superior: Modalidades + Badge de Localidade Ativa */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3">
            <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-1 sm:pb-0 -mx-3 px-3 sm:mx-0 sm:px-0">
              <span className="text-[11px] sm:text-xs font-bold text-muted-foreground uppercase tracking-wider mr-1 shrink-0">
                Modalidade:
              </span>
              <button
                type="button"
                onClick={() => {
                  setSearchParams((prev) => {
                    prev.delete('modality');
                    prev.set('page', '1');
                    return prev;
                  });
                }}
                className={cn(
                  'px-3 py-1.5 text-xs rounded-xl font-bold transition-all shrink-0 cursor-pointer',
                  !selectedModality
                    ? 'bg-primary text-primary-foreground shadow-xs'
                    : 'bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground border border-border/60'
                )}
              >
                Todas
              </button>
              <button
                type="button"
                onClick={() => {
                  setSearchParams((prev) => {
                    prev.set('modality', 'TELEMEDICINA');
                    prev.set('page', '1');
                    return prev;
                  });
                }}
                className={cn(
                  'px-3 py-1.5 text-xs rounded-xl font-bold transition-all shrink-0 flex items-center gap-1.5 cursor-pointer',
                  selectedModality === 'TELEMEDICINA'
                    ? 'bg-primary text-primary-foreground shadow-xs'
                    : 'bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground border border-border/60'
                )}
              >
                <Video className="h-3.5 w-3.5" />
                <span>Telemedicina</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setSearchParams((prev) => {
                    prev.set('modality', 'PRESENCIAL');
                    prev.set('page', '1');
                    return prev;
                  });
                }}
                className={cn(
                  'px-3 py-1.5 text-xs rounded-xl font-bold transition-all shrink-0 flex items-center gap-1.5 cursor-pointer',
                  selectedModality === 'PRESENCIAL'
                    ? 'bg-primary text-primary-foreground shadow-xs'
                    : 'bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground border border-border/60'
                )}
              >
                <Building className="h-3.5 w-3.5" />
                <span>Consultório</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setSearchParams((prev) => {
                    prev.set('modality', 'DOMICILIAR');
                    prev.set('page', '1');
                    return prev;
                  });
                }}
                className={cn(
                  'px-3 py-1.5 text-xs rounded-xl font-bold transition-all shrink-0 flex items-center gap-1.5 cursor-pointer',
                  selectedModality === 'DOMICILIAR'
                    ? 'bg-primary text-primary-foreground shadow-xs'
                    : 'bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground border border-border/60'
                )}
              >
                <Home className="h-3.5 w-3.5" />
                <span>Em Domicílio</span>
              </button>
            </div>

            {/* Badge de Localização Ativa com Botão de Limpar */}
            {(selectedCity || patientCep || addressLabel) && (
              <div className="inline-flex items-center justify-between gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20 shrink-0 self-start sm:self-auto max-w-full">
                <div className="flex items-center gap-1.5 min-w-0">
                  <MapPin className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                  <span className="truncate max-w-[180px] sm:max-w-[220px]">
                    {selectedCity || addressLabel || `CEP: ${patientCep}`}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleClearLocation}
                  className="hover:bg-emerald-500/20 p-0.5 rounded-full ml-1 cursor-pointer transition-colors shrink-0"
                  title="Remover filtro de localidade"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>

          {/* Linha Inferior: Controles de Proximidade (Presencial e Domiciliar) */}
          {(selectedModality === 'PRESENCIAL' || selectedModality === 'DOMICILIAR') && (
            <div className="pt-3 border-t border-border/80 flex flex-col gap-2.5 text-xs animate-in fade-in duration-200">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5">
                <span className="font-bold text-foreground flex items-center gap-1.5 text-xs">
                  <Navigation className="h-3.5 w-3.5 text-primary shrink-0" />
                  <span>
                    {selectedModality === 'DOMICILIAR'
                      ? 'Localização para atendimento em casa:'
                      : 'Localização para consulta em consultório:'}
                  </span>
                </span>

                <span className="text-[11px] text-muted-foreground hidden sm:inline-block">
                  • Apenas médicos no seu raio de atendimento
                </span>
              </div>

              {/* Controles Mobile-First: Botão GPS + Input de CEP com Botão Integrado */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                {/* Botão GPS */}
                <button
                  type="button"
                  onClick={() => requestGpsLocation()}
                  disabled={isLocating}
                  className={cn(
                    'h-9 px-3.5 rounded-xl text-xs font-bold transition-all border flex items-center justify-center gap-2 shrink-0 cursor-pointer',
                    userCoords
                      ? 'border-emerald-500/40 text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 shadow-xs'
                      : 'bg-muted/40 hover:bg-muted text-foreground border-border'
                  )}
                >
                  {isLocating ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-primary shrink-0" />
                      <span>Obtendo GPS...</span>
                    </>
                  ) : (
                    <>
                      <MapPin className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span>{userCoords ? '✓ GPS Ativo' : 'Usar meu local (GPS)'}</span>
                    </>
                  )}
                </button>

                {/* Form de CEP com botão Aplicar embutido */}
                <form onSubmit={handleCepSubmit} className="flex items-center gap-1.5 flex-1 min-w-0">
                  <div className="relative flex-1 min-w-0">
                    <input
                      type="text"
                      placeholder="CEP (ex: 01310-100)"
                      value={cepInput}
                      onChange={(e) => setCepInput(e.target.value)}
                      onBlur={() => cepInput && setCep(cepInput)}
                      maxLength={9}
                      className="w-full h-9 pl-3 pr-2 rounded-xl border border-input bg-background text-xs font-medium text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    />
                  </div>
                  <Button
                    type="submit"
                    size="sm"
                    className="h-9 px-4 text-xs font-bold rounded-xl shrink-0"
                  >
                    Aplicar
                  </Button>
                </form>

                {addressLabel && (
                  <span className="text-[11px] text-emerald-700 dark:text-emerald-400 font-semibold bg-emerald-50 dark:bg-emerald-950/40 px-2.5 py-1 rounded-lg border border-emerald-200 dark:border-emerald-800 truncate max-w-full sm:max-w-xs shrink-0">
                    {addressLabel}
                  </span>
                )}
              </div>

              <span className="text-[10px] text-muted-foreground sm:hidden">
                • Apenas médicos com cobertura no seu raio de localização
              </span>
            </div>
          )}
        </div>

        {/* Botão de Filtros para Mobile (< lg) */}
        <div className="lg:hidden w-full mb-4">
          <button
            type="button"
            onClick={() => setMobileFiltersOpen((prev) => !prev)}
            className="w-full flex items-center justify-between p-3.5 bg-card border border-border rounded-2xl font-bold text-xs shadow-xs hover:border-primary/40 transition-all cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-primary" />
              <span>Filtros (Especialidades e Avaliação)</span>
              {(selectedSpecialty || selectedRating) && (
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-primary text-primary-foreground font-black">
                  Ativo
                </span>
              )}
            </div>
            <ChevronDown
              className={cn(
                'w-4 h-4 transition-transform duration-200 text-muted-foreground',
                mobileFiltersOpen && 'rotate-180'
              )}
            />
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-start w-full">
          <aside className={cn('w-full lg:w-64 flex-shrink-0 transition-all', !mobileFiltersOpen && 'hidden lg:block')}>
            <div className="sticky top-4">
              <div className="bg-card rounded-lg border p-4 mb-4">
                <div className="flex items-center gap-2 mb-4">
                  <Filter className="h-5 w-5" />
                  <h3 className="font-semibold">Especialidades</h3>
                </div>
                <div className="space-y-1.5 max-h-[55vh] overflow-y-auto pr-1">
                  <button
                    onClick={() => handleSpecialtyFilter('')}
                    className={`w-full text-left px-3 py-2 rounded-xl text-xs font-medium transition-colors flex items-center justify-between ${
                      !selectedSpecialty
                        ? 'bg-primary text-primary-foreground font-semibold shadow-xs'
                        : 'hover:bg-muted text-foreground'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Stethoscope className="h-4 w-4 shrink-0" />
                      <span>Todas as especialidades</span>
                    </div>
                    {!selectedSpecialty && <Check className="h-3.5 w-3.5 shrink-0" />}
                  </button>

                  {specialties.map((specialty) => {
                    const isSelected =
                      selectedSpecialty === specialty.id ||
                      selectedSpecialty.toLowerCase() === specialty.name.toLowerCase();
                    return (
                      <button
                        key={specialty.id}
                        onClick={() => handleSpecialtyFilter(specialty.name)}
                        className={`w-full text-left px-3 py-2 rounded-xl text-xs font-medium transition-colors flex items-center justify-between ${
                          isSelected
                            ? 'bg-primary text-primary-foreground font-semibold shadow-xs'
                            : 'hover:bg-muted text-foreground'
                        }`}
                      >
                        <div className="flex items-center gap-2 truncate">
                          {getSpecialtyIcon(specialty.icon)}
                          <span className="truncate">{specialty.name}</span>
                        </div>
                        {isSelected && <Check className="h-3.5 w-3.5 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="bg-card rounded-lg border p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Star className="h-5 w-5" />
                  <h3 className="font-semibold">Avaliação Mínima</h3>
                </div>
                <div className="space-y-2">
                  {[
                    { value: '', label: 'Qualquer' },
                    { value: '4', label: '4+ estrelas' },
                    { value: '3', label: '3+ estrelas' },
                    { value: '2', label: '2+ estrelas' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setSearchParams((prev) => {
                          if (opt.value) {
                            prev.set('rating', opt.value);
                          } else {
                            prev.delete('rating');
                          }
                          prev.set('page', '1');
                          return prev;
                        });
                      }}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                        selectedRating === opt.value
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-muted'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          <main className="flex-1 w-full min-w-0">
            {isLoading ? (
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 w-full">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Card key={i} className="p-4 sm:p-5">
                    <div className="flex items-start gap-4">
                      <Skeleton className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl" />
                      <div className="flex-1 min-w-0">
                        <Skeleton className="h-5 w-32 mb-2" />
                        <Skeleton className="h-4 w-24 mb-2" />
                        <Skeleton className="h-4 w-20" />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : doctors.length === 0 ? (
              <EmptyState
                icon="Stethoscope"
                title="Nenhum médico encontrado"
                description={
                  searchQuery || selectedCity
                    ? 'Tente remover o filtro de localidade ou buscar por outra especialidade'
                    : 'Não há médicos disponíveis com os filtros selecionados'
                }
                action={{ label: 'Limpar Filtros', onClick: handleClearAllFilters }}
              />
            ) : (
              <>
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 w-full">
                  {doctors.map((doctor) => (
                    <DoctorSearchCard
                      key={doctor.id}
                      doctor={doctor}
                      onNavigate={(id) => navigate(`/medicos/${id}`)}
                      renderStars={renderStars}
                    />
                  ))}
                </div>

                {pagination.pages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8 flex-wrap">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage <= 1}
                      className="rounded-xl h-9 text-xs"
                    >
                      <ChevronLeft className="h-3.5 w-3.5 mr-1" />
                      Anterior
                    </Button>
                    <span className="text-xs sm:text-sm text-muted-foreground px-2">
                      Página {currentPage} de {pagination.pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= pagination.pages}
                      className="rounded-xl h-9 text-xs"
                    >
                      Próxima
                      <ChevronRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
      <LocationPromptModal />
    </div>
  );
}

const AVATAR_GRADIENTS = [
  'bg-gradient-to-br from-blue-600 to-indigo-600 text-white',
  'bg-gradient-to-br from-emerald-600 to-teal-600 text-white',
  'bg-gradient-to-br from-purple-600 to-indigo-700 text-white',
  'bg-gradient-to-br from-cyan-600 to-blue-700 text-white',
  'bg-gradient-to-br from-amber-600 to-orange-600 text-white',
  'bg-gradient-to-br from-rose-600 to-pink-600 text-white',
];

function getDoctorInitials(name: string): string {
  const cleaned = name.replace(/^(dr\.|dra\.|dr|dra)\s+/i, '').trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  if (!first) return 'D';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  return `${first[0]?.toUpperCase() ?? ''}${last[0]?.toUpperCase() ?? ''}`;
}

interface DoctorSearchCardProps {
  doctor: DoctorListItem;
  onNavigate: (id: string) => void;
  renderStars: (rating: number) => React.ReactNode;
}

function DoctorSearchCard({ doctor, onNavigate, renderStars }: DoctorSearchCardProps) {
  const [imgError, setImgError] = useState(false);

  const rawPhoto = doctor.profile?.photoUrl;
  const photoUrl = rawPhoto && !imgError ? filesApi.getFileUrl(rawPhoto) : null;
  const docName = doctor.profile?.displayName || 'Dr(a). Profissional';
  const specName = doctor.primarySpecialty?.name || 'Clínica Geral';
  const initials = getDoctorInitials(docName);

  // Deterministic gradient selection based on doctor ID/name
  const colorIndex = useMemo(() => {
    let hash = 0;
    const str = doctor.id || docName;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash) % AVATAR_GRADIENTS.length;
  }, [doctor.id, docName]);

  const avatarGradient = AVATAR_GRADIENTS[colorIndex];

  const priceValue =
    doctor.startingPrice ??
    (doctor.consultationPrice != null
      ? doctor.consultationPrice >= 1000
        ? doctor.consultationPrice / 100
        : doctor.consultationPrice
      : 120);
  const formattedPrice = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(priceValue);

  return (
    <Card
      className="p-4 sm:p-5 hover:shadow-lg hover:border-primary/40 transition-all cursor-pointer rounded-2xl flex flex-col justify-between w-full group"
      onClick={() => onNavigate(doctor.id)}
    >
      <div>
        {/* Header: Priority / Partner Badge if configured */}
        {doctor.priorityBadge && (
          <div className="mb-3">
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-gradient-to-r from-blue-600/15 via-indigo-600/20 to-purple-600/15 text-blue-800 dark:text-blue-300 border border-blue-500/30 shadow-2xs inline-flex items-center gap-1">
              <Sparkles className="w-2.5 h-2.5 text-blue-600 dark:text-blue-400" />
              <span>{doctor.priorityBadge}</span>
            </span>
          </div>
        )}

        <div className="flex items-start gap-3.5 sm:gap-4">
          <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-muted flex items-center justify-center overflow-hidden shrink-0 border border-border shadow-xs relative">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={docName}
                onError={() => setImgError(true)}
                className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div
                className={cn(
                  'w-full h-full flex items-center justify-center font-black text-sm sm:text-base tracking-wider',
                  avatarGradient
                )}
              >
                {initials}
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-foreground text-sm sm:text-base truncate group-hover:text-primary transition-colors">
              {docName}
            </h3>
            <p className="text-xs sm:text-sm font-semibold text-primary truncate">
              {specName}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
              CRM {doctor.crm} - {doctor.crmState || 'SP'}
            </p>
            {doctor.clinicCity && (
              <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5 truncate">
                <Building className="h-3 w-3 text-primary shrink-0" />
                <span className="truncate">
                  {doctor.clinicNeighborhood ? `${doctor.clinicNeighborhood}, ` : ''}{doctor.clinicCity}
                </span>
              </p>
            )}
            {doctor.statistics && renderStars(doctor.statistics.averageRating)}
          </div>
        </div>

        {doctor.distanceKm != null && !isNaN(Number(doctor.distanceKm)) && (
          <div className="mt-3">
            <Badge className="bg-emerald-600 text-white font-semibold text-xs flex items-center gap-1 w-fit">
              <MapPin className="h-3 w-3" />
              {Number(doctor.distanceKm).toFixed(1)} km de você
            </Badge>
          </div>
        )}

        {doctor.profile?.tagline && (
          <p className="mt-3 text-xs text-muted-foreground line-clamp-2">
            {doctor.profile.tagline}
          </p>
        )}

        <div className="mt-3 flex flex-wrap gap-1.5">
          {doctor.hasTelemedicine && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-cyan-50 dark:bg-cyan-950/40 text-cyan-700 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-800">
              <Video className="w-2.5 h-2.5" />
              Telemedicina
            </span>
          )}
          {doctor.hasInPerson && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
              <Building className="w-2.5 h-2.5" />
              Consultório
            </span>
          )}
          {doctor.hasHomeCare && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
              <Home className="w-2.5 h-2.5" />
              Domiciliar
            </span>
          )}
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-border/70 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
        <div>
          <span className="text-[10px] text-muted-foreground block uppercase font-bold tracking-wider">A partir de</span>
          <span className="text-sm sm:text-base font-bold text-foreground">
            {formattedPrice}
          </span>
        </div>
        <Button size="sm" className="h-9 px-4 text-xs rounded-xl font-semibold w-full sm:w-auto">
          Ver Perfil e Agenda
        </Button>
      </div>
    </Card>
  );
}
